import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Banknote, Printer, Undo2 } from "lucide-react";
import { formatMoney } from "@finora/config";
import { EmptyState, Skeleton, StatusBadge } from "@finora/ui";
import type { FiscalDocumentDto } from "@finora/types";
import { api } from "../../lib/api";
import { randomUUID } from "../../lib/uuid";
import { usePosStore } from "../../store/pos-store";
import { ReceiptView } from "../Pos/ReceiptView";

/** Dodatno polje koje server šalje SAMO na ovoj listi (Task 7, razgovor
 * 28.09.2026) - namerno nije u @finora/types FiscalDocumentDto, vidi
 * napomenu u FiscalDocumentsService.list(). */
type FiscalDocumentListItem = FiscalDocumentDto & { hasClosingInvoice?: boolean };

const STATUS_LABEL: Record<string, string> = {
  DRAFT: "Nacrt",
  PAYMENT_PENDING: "Čeka uplatu",
  READY_FOR_FISCALIZATION: "Spreman za fiskalizaciju",
  FISCALIZED: "Fiskalizovano",
  FISCALIZATION_FAILED: "Neuspela fiskalizacija",
  CANCELLED: "Otkazano",
  REFUNDED: "Stornirano",
};
const STATUS_TONE: Record<string, "success" | "warning" | "neutral"> = {
  DRAFT: "neutral",
  PAYMENT_PENDING: "neutral",
  READY_FOR_FISCALIZATION: "warning",
  FISCALIZED: "success",
  FISCALIZATION_FAILED: "warning",
  CANCELLED: "neutral",
  REFUNDED: "neutral",
};

// NAPOMENA: koristi lokalne komponente datuma (ne .toISOString(), koji je
// UTC) - inace "Danas" filter na Fiskalizacija/Poslednji racuni moze da ne
// prikaze danasnje racune uvece po Beograd vremenu (UTC dan se vec promenio
// dok je lokalni dan jos "danas"). Video: razgovor 30.09.2026.
function toIsoDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

type PeriodPreset = "today" | "this-month" | "custom";
type StatusFilter = "" | "DRAFT" | "FISCALIZED";

// "Svi" u filteru statusa NAMERNO ne uključuje Nacrt (razgovor 23.09.2026,
// "kada se fiskalizuje da se obrise nacrt da ne buni korisnike") - nacrt
// (DRAFT) nije stvarno izdat fiskalni račun, pa mu ovde nije mesto u
// podrazumevanom prikazu "Poslednji računi" (nefiskalizovan/napušten nacrt
// bi inače zauvek ostajao vidljiv pored pravih računa i delovao kao greška).
// Kasir i dalje može eksplicitno da izabere "Nacrt" iz padajućeg menija da
// pronađe nezavršenu korpu - baza/PFR logika ovde nije dirana, ovo je samo
// podrazumevana vrednost filtera na listi.
const NON_DRAFT_STATUSES =
  "PAYMENT_PENDING,READY_FOR_FISCALIZATION,FISCALIZED,FISCALIZATION_FAILED,CANCELLED,REFUNDED";

export function FiscalDocumentsListPage({ onAdvanceClosed }: { onAdvanceClosed?: () => void } = {}) {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState<PeriodPreset>("today");
  const [customFrom, setCustomFrom] = useState(toIsoDate(new Date()));
  const [customTo, setCustomTo] = useState(toIsoDate(new Date()));
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [pageSize, setPageSize] = useState<number | "all">(20);
  const [page, setPage] = useState(0);
  const [viewingDoc, setViewingDoc] = useState<FiscalDocumentDto | null>(null);

  const range =
    period === "today"
      ? { from: toIsoDate(new Date()), to: toIsoDate(new Date()) }
      : period === "this-month"
        ? {
            from: toIsoDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
            to: toIsoDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)),
          }
        : { from: customFrom, to: customTo };

  const { data, isLoading } = useQuery({
    queryKey: ["fiscal-documents-list", range.from, range.to, statusFilter, pageSize, page],
    queryFn: () =>
      api.listFiscalDocuments({
        status: statusFilter === "" ? NON_DRAFT_STATUSES : statusFilter,
        from: range.from,
        to: range.to,
        limit: pageSize,
        offset: pageSize === "all" ? 0 : page * pageSize,
      }),
  });

  const [refundError, setRefundError] = useState<string | null>(null);

  const refund = useMutation({
    mutationFn: (id: string) => api.refundFiscalDocument(id, randomUUID()),
    onSuccess: (updated) => {
      // Backend NAMERNO ne baca grešku kad refundacija ne uspe (isti obrazac
      // kao fiscalize()) - status ostaje FISCALIZED umesto REFUNDED, a
      // razlog je u poslednjem fiscalizationAttempts redu. Bez ove provere,
      // neuspeo storno je delovao kao da se "ništa nije desilo".
      if (updated.status !== "REFUNDED") {
        setRefundError(
          updated.fiscalizationAttempts.at(-1)?.providerMessage ?? "Storno nije uspeo.",
        );
      } else {
        setRefundError(null);
      }
      queryClient.invalidateQueries({ queryKey: ["fiscal-documents-list"] });
    },
    onError: (error) => {
      setRefundError(error instanceof Error ? error.message : "Storno nije uspeo.");
    },
  });

  // Task #38 - "Storno avansnog računa zbog izdavanja gotovinskog" (posebna
  // opcija - NIJE isto što i refund iznad). Uspešno kreira NACRT novog
  // gotovinskog (PROMET) računa povezanog sa avansom (referencira Аванс
  // Рефундација po PFR lancu) - taj nacrt se postavlja kao aktivan dokument
  // na kasi (usePosStore), kasir prelazi na tab "Kasa" da naplati/fiskalizuje.
  const [closeAdvanceError, setCloseAdvanceError] = useState<string | null>(null);
  const [closeAdvanceSuccess, setCloseAdvanceSuccess] = useState<string | null>(null);
  const closeAdvance = useMutation({
    mutationFn: (id: string) => api.closeAdvanceWithCashSale(id, randomUUID()),
    onSuccess: (cashSaleDraft) => {
      usePosStore.getState().setActiveDocumentId(cashSaleDraft.id);
      setCloseAdvanceError(null);
      // Odmah na Kasu: tamo se biraju stvarni artikli i naplaćuje doplata.
      onAdvanceClosed?.();
      setCloseAdvanceSuccess(
        `Avansni lanac je zatvoren (Аванс Рефундација izdata). Kreiran je nacrt gotovinskog računa - pređite na tab "Kasa" da ga naplatite i fiskalizujete.`,
      );
      queryClient.invalidateQueries({ queryKey: ["fiscal-documents-list"] });
    },
    onError: (error) => {
      setCloseAdvanceSuccess(null);
      setCloseAdvanceError(
        error instanceof Error ? error.message : "Zatvaranje avansa gotovinskim računom nije uspelo.",
      );
    },
  });

  const totalPages = data && pageSize !== "all" ? Math.ceil(data.total / pageSize) : 1;

  // Avansni (AVANS) računi čiji je storno bio ZATVARANJE avansa gotovinskim
  // računom (Task #38, closeAdvance iznad) treba da imaju drugačiju oznaku
  // statusa od običnog storna (refund dugme) - vidi razgovor 23.09.2026.
  // Oznaku sada računa server (FiscalDocumentsService.list(),
  // `hasClosingInvoice`, Task 7, razgovor 28.09.2026) proverom CELE firme
  // preko linkedAdvanceDocumentId, a ne samo trenutno učitane stranice kao
  // ranije - vidi napomenu u tom fajlu.
  // Zapis koji je napravljen ali nikad nije poslat na fiskalizaciju (nema fiskalnog broja)
  // nije račun - posle 30 min se prikazuje kao "Nije fiskalizovan", samo za pregled.
  function isAbandoned(doc: FiscalDocumentListItem): boolean {
    if (doc.status !== "READY_FOR_FISCALIZATION" || doc.fiscalNumber) return false;
    const created = doc.createdAt ? new Date(doc.createdAt).getTime() : 0;
    return created > 0 && Date.now() - created > 30 * 60_000;
  }

  function statusLabelFor(doc: FiscalDocumentListItem): string {
    if (isAbandoned(doc)) return "Nije fiskalizovan";
    if (doc.status === "REFUNDED" && doc.pfrDocumentType === "AVANS" && doc.hasClosingInvoice) {
      return "Storno Avans → Konačni račun";
    }
    return STATUS_LABEL[doc.status] ?? doc.status;
  }

  return (
    <div className="space-y-4">
      {refundError && (
        <div className="flex items-start justify-between rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>
            <strong>Storno nije uspeo:</strong> {refundError}
          </span>
          <button onClick={() => setRefundError(null)} className="ml-3 text-red-400 hover:text-red-600">
            ×
          </button>
        </div>
      )}
      {closeAdvanceError && (
        <div className="flex items-start justify-between rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>
            <strong>Zatvaranje avansa nije uspelo:</strong> {closeAdvanceError}
          </span>
          <button
            onClick={() => setCloseAdvanceError(null)}
            className="ml-3 text-red-400 hover:text-red-600"
          >
            ×
          </button>
        </div>
      )}
      {closeAdvanceSuccess && (
        <div className="flex items-start justify-between rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          <span>{closeAdvanceSuccess}</span>
          <button
            onClick={() => setCloseAdvanceSuccess(null)}
            className="ml-3 text-emerald-500 hover:text-emerald-700"
          >
            ×
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <div className="flex gap-1">
          {(
            [
              { key: "today", label: "Danas" },
              { key: "this-month", label: "Ovog meseca" },
              { key: "custom", label: "Od - do" },
            ] as { key: PeriodPreset; label: string }[]
          ).map((p) => (
            <button
              key={p.key}
              onClick={() => {
                setPeriod(p.key);
                setPage(0);
              }}
              className={`rounded-lg px-3 py-2 text-sm font-medium ${
                period === p.key ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex items-end gap-2">
            <input
              type="date"
              value={customFrom}
              onChange={(e) => {
                setCustomFrom(e.target.value);
                setPage(0);
              }}
              className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
            <span className="text-slate-400">—</span>
            <input
              type="date"
              value={customTo}
              onChange={(e) => {
                setCustomTo(e.target.value);
                setPage(0);
              }}
              className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
        )}

        <div className="ml-auto flex items-end gap-2">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as StatusFilter);
              setPage(0);
            }}
            className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          >
            <option value="">Svi</option>
            <option value="DRAFT">Nacrt</option>
            <option value="FISCALIZED">Fiskalizovani</option>
          </select>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(e.target.value === "all" ? "all" : Number(e.target.value));
              setPage(0);
            }}
            className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          >
            <option value={10}>Prikaži 10</option>
            <option value={20}>Prikaži 20</option>
            <option value={30}>Prikaži 30</option>
            <option value={40}>Prikaži 40</option>
            <option value="all">Prikaži sve</option>
          </select>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="p-5">
            <EmptyState icon={AlertTriangle} title="Nema računa za izabrani period/filter" />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Broj (PFR)</th>
                <th>Datum</th>
                <th>Iznos</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.items.map((doc) => (
                <tr key={doc.id} className={isAbandoned(doc) || !doc.fiscalNumber ? "" : "cursor-pointer hover:bg-slate-50"} onClick={() => !isAbandoned(doc) && !!doc.fiscalNumber && setViewingDoc(doc)}>
                  <td className="font-medium text-slate-800">
                    {doc.fiscalNumber ?? doc.documentNumber ?? "—"}
                  </td>
                  <td className="text-slate-500">
                    {new Date(doc.fiscalizedAt ?? doc.createdAt ?? Date.now()).toLocaleString("sr-RS")}
                  </td>
                  <td>{formatMoney(doc.grossAmount, doc.currency)}</td>
                  <td>
                    <StatusBadge
                      label={statusLabelFor(doc)}
                      tone={isAbandoned(doc) ? "neutral" : (STATUS_TONE[doc.status] ?? "neutral")}
                    />
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      {!isAbandoned(doc) && !!doc.fiscalNumber && (
                      <button
                        onClick={() => setViewingDoc(doc)}
                        className="text-slate-400 hover:text-brand"
                        title="Prikaži / odštampaj"
                      >
                        <Printer className="h-4 w-4" aria-hidden />
                      </button>
                      )}
                      {doc.status === "FISCALIZED" && doc.fiscalProcessor === "L_PFR" && (
                        <span
                          className="text-xs text-slate-400"
                          title="Račun je izdat preko L-PFR-a. Storno i kopija se rade iz kase koja ga je izdala."
                        >
                          Storno iz kase
                        </span>
                      )}
                      {doc.status === "FISCALIZED" && doc.fiscalProcessor !== "L_PFR" && (
                        <button
                          onClick={() => {
                            if (window.confirm("Stornirati (refundirati) ovaj račun?")) {
                              refund.mutate(doc.id);
                            }
                          }}
                          disabled={refund.isPending}
                          className="text-slate-400 hover:text-red-600"
                          title="Storno"
                        >
                          <Undo2 className="h-4 w-4" aria-hidden />
                        </button>
                      )}
                      {doc.status === "FISCALIZED" && doc.fiscalProcessor !== "L_PFR" && doc.pfrDocumentType === "AVANS" && (
                        // Task #38 - posebna opcija (odvojena od običnog Storno
                        // dugmeta iznad): zatvara avansni PFR lanac (Аванс
                        // Рефундација) i priprema nacrt gotovinskog računa.
                        <button
                          onClick={() => {
                            if (
                              window.confirm(
                                "Zatvoriti ovaj avansni račun izdavanjem gotovinskog (Аванс Рефундација + novi Промет Продаја)?",
                              )
                            ) {
                              closeAdvance.mutate(doc.id);
                            }
                          }}
                          disabled={closeAdvance.isPending}
                          className="text-slate-400 hover:text-emerald-600"
                          title="Storno avansnog zbog izdavanja gotovinskog"
                        >
                          <Banknote className="h-4 w-4" aria-hidden />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pageSize !== "all" && data && data.total > pageSize && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>
            Strana {page + 1} od {totalPages} ({data.total} ukupno)
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-lg border border-border px-3 py-1.5 disabled:opacity-40"
            >
              Prethodna
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="rounded-lg border border-border px-3 py-1.5 disabled:opacity-40"
            >
              Sledeća
            </button>
          </div>
        </div>
      )}

      {viewingDoc && (
        <ReceiptView document={viewingDoc} onClose={() => setViewingDoc(null)} allowCopyPrint />
      )}
    </div>
  );
}
